/* eslint-disable max-lines */

import { useSelector } from "react-redux";

import { useString } from "../localization/localizationHelpers";
import {
  get3dSpatialSoundList,
  getAustraliaAcbRatings,
  getBrazilRatings,
  getEsrbRatings,
  getEuropeIsfeRatings,
  getGenres,
  getGermanyUskRatings,
  getJapanCeroRatings,
  getKoreaGrbRatings,
  getMiddlewareEngines,
  getNewZealandOflcRatings,
  getRatings,
  getRussiaPcbpRatings,
  getSaudiArabiaGamrRatings,
  getTaiwanCsrrRatings,
} from "../redux/selectors";
import { FieldOption } from "../server/crm/api/models/forms";

export type DropdownOption = Readonly<{
  value: string | number | undefined;
  label: string;
}>;

const useGenerateList = (options: FieldOption[]): Array<DropdownOption> => {
  const select = useString("select");
  const buildingList: Array<DropdownOption> = [{ value: 0, label: select }];
  options?.forEach(option => {
    buildingList.push({
      value: option.id,
      label: option.value,
    });
  });

  return buildingList;
};

export const useGenresList = (): Array<DropdownOption> => {
  const crmGenresList = useSelector(getGenres);
  const result = useGenerateList(crmGenresList);
  return result;
};

export const useMiddlewareEnginesList = (): Array<DropdownOption> => {
  const crmMiddlewareEnginesList = useSelector(getMiddlewareEngines);
  const otherNone = useString("middlewareOtherNone");
  const result = useGenerateList(crmMiddlewareEnginesList);
  return result.map(option =>
    option.label === "Other" ? { ...option, label: otherNone } : option
  );
};

export const useRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useEsrbRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getEsrbRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useGermanyUskRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getGermanyUskRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useTaiwanCsrrRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getTaiwanCsrrRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useEuropeIsfeRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getEuropeIsfeRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useAustraliaAcbRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getAustraliaAcbRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useRussiaPcbpRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getRussiaPcbpRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useJapanCeroRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getJapanCeroRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useKoreaGrbRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getKoreaGrbRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useSaudiArabiaGamrRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getSaudiArabiaGamrRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useBrazilRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getBrazilRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const useNewZealandOflcRatingsList = (): Array<DropdownOption> => {
  const crmRatingsList = useSelector(getNewZealandOflcRatings);
  const result = useGenerateList(crmRatingsList);
  return result;
};

export const use3dSpatialSoundList = (): Array<DropdownOption> => {
  const spatial3dSoundList = useSelector(get3dSpatialSoundList);
  const result = useGenerateList(spatial3dSoundList);
  return result;
};

export const useStateOrRegionList = (options: string[] | undefined) => {
  const select = useString("select");
  if (!options) {
    return null;
  }
  const buildingList: Array<DropdownOption> = [{ value: "", label: select }];
  const sorted = [...options].sort((a, b) => a.localeCompare(b));
  sorted.forEach(option => {
    buildingList.push({
      label: option,
      value: option,
    });
  });

  return buildingList;
};
